//============================================================================
// Name        : index.ts
// Purpose     : Implement Rails per page typescript coding
// Created     : 2020-01-02
// Author      : Senitron Team
// Copyright   : Copyright 2020 Senitron Corporation. All rights reserved.
//============================================================================
import * as ApiMonitor from "./api_monitor/api_monitor";
import * as AssetDetails from "./items/asset_details";
import * as BulkPrint from "./printing/bulk_print";
import * as CatalogExportProgressUpdate from "./items/catalog_export_progress_update";
import * as CatalogSidebar from "./items/catalog_sidebar";
import * as CdmIndex from "./cdm/index";
import * as DeleteDialog from "./common/delete_dialog";
import * as ConfirmDialog from "./common/confirm_dialog";
import * as Disposal from "./manufacturing/disposal";
import * as ElcAlarm from "./elc/alarm";
import * as ElcEmployees from "./elc/employees";
import * as ElcPlanningCalendar from "./elc/planning_calendar";
import * as ElcProductionCalendar from "./elc/productions/productions_calendar";
import * as ElcProductionCards from "./elc/productions/cards";
import * as ElcProductionCharts from "./elc/productions/charts";
import * as ElcProductionEmployeeLogTime from "./elc/productions/employee_log_time";
import * as ElcProductionHelpRequest from "./elc/productions/help_requests";
import * as ElCProductionLiveUpdateCard from "./elc/productions/live_update_card";
import * as ElcProductionNew from "./elc/productions/new";
import * as ElcStandard from "./elc/standard";
import * as ElcTempAgencyAssign from "./elc/temp_agency_assign";
import * as ElcTempAgencyCheckin from "./elc/temp_agency_checkin";
import * as ErcPurchaseOrderReceivings from "./erc/purchase_order_receivings";
import * as ExcludedCategories from "./lightspeed/excluded_categories";
import * as ExclusionSidebar from "./lightspeed/exclusion_side_bar";
import * as ExitAlertsView from "./exit_alerts/view";
import * as ExternalMessageAlert from "./external/expermient_messages";
import * as HandheldApiLogs from "./handheld/api_logs/api_logs";
import * as HeartlandProgressUpdate from "./heartland/heartland_progress_update";
import * as Integrations from "./integrations/integrations";
import * as Inventory from "./epc_tags/inventory";
import * as InventoryPushToHandheld from "./epc_tags/push_to_handheld";
import * as ItemGroup from "./items/item_group";
import * as LightspeedModalCategories from "./lightspeed/modal_categories";
import * as LightspeedSidebar from "./lightspeed/sidebar";
import * as LiveSalesOrders from "./so_processes/live_so_processes";
import * as LoadingDocks from "./pallet_build/loading_docks";
import * as LsCompare from "./lightspeed/ls_compare";
import * as LsProgressUpdate from "./lightspeed/ls_progress_update";
import * as LsUndoProgressUpdate from "./lightspeed/ls_undo_progress_update";
import * as ManufacturingProductionCalendar from "./manufacturing/production/calendar";
import * as ManufacturingProductionReconciliation from "./manufacturing/production/reconciliation";
import * as MfQuickShipOut from "./manufacturing/sales_orders/quick_ship_out";
import * as MfVerifyShipping from "./manufacturing/sales_orders/verify_shipping";
import * as MfSoAllocations from "./manufacturing/sales_orders/allocations";
import * as MfWebPushLogs from "./manufacturing/web_push_logs";
import * as NavErrorTemplate from "./nav/error_template";
import * as Partners from "./partners/partners";
import * as QualityHold from "./manufacturing/quality_hold";
import * as Returns from "./manufacturing/sales_orders/returns";
import * as Rfid from "./rfid/rfid";
import * as RfidPrint from "./rfid/print";
import * as RfidPrintLsPoItems from "./rfid/print_ls_po_items";
import * as RfidPrintR2 from "./rfid/print_r2";
import * as RfidTagging from "./manufacturing/rfid_tagging";
import * as SaveWOState from "./manufacturing/production/save_wo_state";
import * as Shipments from "./transactions/shipments";
import * as ShipmentsTransferDetails from "./transactions/shipments_transfer_details";
import * as Shippings from "./pallet_build/cartons";
import * as ShopifyProgressUpdate from "./shopify/shopify_progress_update";
import * as QuickbookProgressUpdate from "./quickbook/quickbook_progress_update";
import * as Sidebar from "./epc_tags/sidebar";
import * as SummarizationStats from "./logs/summarization_stats";
import * as TenantHandheldSettings from "./handheld/tenant_handheld_settings";
import * as TheftAlertInstant from "./theft_alerts/instant";
import * as TzProgressUpdate from "./treez/tz_progress_update";
import * as VendProgressUpdate from "./vend/vend_progress_update";
import * as Customers from "./customers/customers";
import * as ELCLiveEmployeeCount from "./elc/productions/live_employee_count";
import * as MfBinManagement from "./manufacturing/bin_management";
import * as ELCProductionsLiveAlarm from "./elc/productions/live_alarm";
import * as ManufacturingSoCount from "./manufacturing/sales_orders/so_count";
import * as ShippingShipments from "./shipping/shipments/shipments";
import * as ShippingPickings from "./shipping/pickings/pickings";
import * as History from "./manufacturing/so_processes/history";
import * as PrintAction from "./manufacturing/so_processes/print_action";
import * as CustomModulesBin from "./custom_modules/bin";
import * as IRMSInventoryRequests from "./irms/inventory_requests";
import * as IRMSRequestEpcs from "./irms/request_epcs";
import * as EOSWebhookLogs from "./eos/webhook_logs";
import * as AssetMovements from "./reports_and_logs/asset_movements";
import * as ToasterNotifications from "./common/toaster_notifications";
import * as AccessLog from "./users/access_log";
import * as InventoryValuation from "./custom_reports/inventory_valuation";
import * as MfApiLogs from "./manufacturing/api_logs";
import * as cdmHhIndex from "./cdm_handhelds";
import * as ManufacturingTagsGenerate from "./manufacturing/tags/generate";
import * as EOSTransferReceivingSearch from "./eos/transfer/receiving/search";
import * as ERCTransfer from "./erc/transfer";

/// Execute module only on the specified page
export function InitPage(pageClass: string, initFunc: () => void): void {
    //-- jQuery Helpers

    // Case insensitive Contains
    $.expr.pseudos.contains = $.expr.createPseudo((arg) => {
        return (elem) => {
            return $(elem).text().toUpperCase().indexOf(arg.toUpperCase()) >= 0;
        };
    });

    const classes = pageClass.split(" ");
    let pageFound = false;
    const bodyEl = document.querySelector("body");

    // The first class is the controller, so we always want to make sure
    // we're loading any given page on the controller context
    if (bodyEl.classList.contains(classes[0])) {
        for (const c of classes) {
            if (bodyEl.classList.contains(c)) {
                pageFound = true;
            }
        }
    }

    if (!pageFound) {
        return;
    }

    // Execute our page initialization code
    initFunc();
}

export function InitApp() {
    ToasterNotifications.LoadPage();

    DeleteDialog.LoadPage();
    ConfirmDialog.LoadPage();

    // Load all registered pages
    Sidebar.LoadPage();
    LightspeedSidebar.LoadPage();
    ExclusionSidebar.LoadPage();
    ExcludedCategories.LoadPage();
    LightspeedModalCategories.LoadPage();
    LsProgressUpdate.LoadPage();
    TzProgressUpdate.LoadPage();
    VendProgressUpdate.LoadPage();
    ShopifyProgressUpdate.LoadPage();
    QuickbookProgressUpdate.LoadPage();
    HeartlandProgressUpdate.LoadPage();
    LsUndoProgressUpdate.LoadPage();
    CatalogExportProgressUpdate.LoadPage();
    CatalogSidebar.LoadPage();
    ItemGroup.LoadPage();
    HandheldApiLogs.LoadPage();
    TenantHandheldSettings.LoadPage();
    ApiMonitor.LoadPage();
    Integrations.LoadPage();
    Inventory.LoadPage();
    InventoryPushToHandheld.LoadPage();
    AssetDetails.LoadPage();
    LsCompare.LoadPage();
    MfSoAllocations.LoadPage();
    MfQuickShipOut.LoadPage();
    MfVerifyShipping.LoadPage();
    TheftAlertInstant.LoadPage();
    ExternalMessageAlert.LoadPage();
    LiveSalesOrders.LoadPage();
    ExitAlertsView.LoadPage();
    BulkPrint.LoadPage();
    QualityHold.LoadPage();
    Disposal.LoadPage();
    RfidTagging.LoadPage();
    Partners.LoadPage();
    Shipments.LoadPage();
    ShipmentsTransferDetails.LoadPage();
    Shippings.LoadPage();
    LoadingDocks.LoadPage();
    Customers.LoadPage();
    ShippingShipments.LoadPage();
    ShippingPickings.LoadPage();
    CustomModulesBin.LoadPage();
    AssetMovements.LoadPage();
    InventoryValuation.LoadPage();
    MfApiLogs.LoadPage();
    cdmHhIndex.LoadPage();

    // RRD - CDM
    CdmIndex.LoadPage();

    // RFID Pages
    Rfid.LoadPage();
    RfidPrint.LoadPage();
    RfidPrintR2.LoadPage();
    RfidPrintLsPoItems.LoadPage();

    // Reports and Logs
    SummarizationStats.LoadPage();
    Returns.LoadPage();
    MfWebPushLogs.LoadPage();
    MfBinManagement.LoadPage();

    // WO Production
    SaveWOState.LoadPage();
    ManufacturingProductionReconciliation.LoadPage();
    ManufacturingProductionCalendar.LoadPage();
    ManufacturingSoCount.LoadPage();

    // ELC Common
    ElcStandard.LoadPage();
    ElcEmployees.LoadPage();
    ElcAlarm.LoadPage();
    ElcPlanningCalendar.LoadPage();
    ElcProductionCards.LoadPage();
    ElcProductionCharts.LoadPage();
    ElcTempAgencyAssign.LoadPage();
    ElcTempAgencyCheckin.LoadPage();
    ELCLiveEmployeeCount.LoadPage();

    ElcProductionNew.LoadPage();
    ElcProductionHelpRequest.LoadPage();
    ElcProductionEmployeeLogTime.LoadPage();
    ElcProductionCalendar.LoadPage();
    ElCProductionLiveUpdateCard.LoadPage();
    ELCProductionsLiveAlarm.LoadPage();

    // ERC
    ErcPurchaseOrderReceivings.LoadPage();

    // IRMS
    IRMSInventoryRequests.LoadPage();
    IRMSRequestEpcs.LoadPage();

    // EOS
    EOSWebhookLogs.LoadPage();
    EOSTransferReceivingSearch.LoadPage();

    // Manufacturing
    ManufacturingTagsGenerate.LoadPage();

    NavErrorTemplate.LoadPage();
    History.LoadPage();
    PrintAction.LoadPage();
    AccessLog.LoadPage();

    // ERC
    ERCTransfer.LoadPage();
}
